import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/index.js';
import { NotificationBell } from './notification-bell.js';
import { LogoMark } from './logo.js';
import { useTheme } from './theme-provider.js';
import { AppMenu } from './app-menu.js';

const STORAGE_KEY = 'sidebar-collapsed';


function ThemeToggle({ collapsed }: { collapsed: boolean }) {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const cycle = () => {
    const next: Record<string, 'dark' | 'light' | 'auto'> = { auto: 'dark', dark: 'light', light: 'auto' };
    setTheme(next[theme]);
  };
  return (
    <button
      type="button"
      onClick={cycle}
      title={`Theme: ${theme}`}
      aria-label="Toggle theme"
      className="sidebar-action"
    >
      <span className="sidebar-action-icon">
        {resolvedTheme === 'dark' ? (
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <circle cx="12" cy="12" r="5" /><path strokeLinecap="round" d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
          </svg>
        ) : (
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
          </svg>
        )}
      </span>
      {!collapsed && <span className="sidebar-action-label">{theme}</span>}
    </button>
  );
}

export function Sidebar() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, String(next)); } catch { /* noop */ }
      return next;
    });
  }, []);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === '[' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggle();
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [toggle]);

  return (
    <aside
      className="sidebar"
      style={{ width: collapsed ? 56 : 216 }}
      data-collapsed={collapsed}
    >
      {/* Logo */}
      <div className="sidebar-logo">
        <Link to="/" style={{ textDecoration: 'none' }}>
          <div className="flex items-center gap-2.5">
            <LogoMark size={collapsed ? 24 : 28} />
            {!collapsed && (
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
                <span className="logo-ai" style={{ fontSize: 18 }}>AI</span>
                <span className="logo-dev" style={{ fontSize: 9 }}>DEV</span>
              </div>
            )}
          </div>
        </Link>
      </div>

      {/* The server shown, what waits on you there, its pages; then what is every server's */}
      <AppMenu collapsed={collapsed} />

      {/* Bottom actions */}
      <div className="sidebar-bottom">
        {/* Theme, notifications and account: one row when unfolded, the menu above keeps the height */}
        <div className={collapsed ? 'sidebar-bottom-stack' : 'sidebar-bottom-row'}>
          <ThemeToggle collapsed />

          <div className="sidebar-action-bell sidebar-action-bell-collapsed">
            <NotificationBell placement="side" />
          </div>

          {user && (
            <button
              type="button"
              onClick={logout}
              className="sidebar-action"
              title={`Sign out ${user.email ?? user.sub}`}
              aria-label={`Sign out ${user.email ?? user.sub}`}
            >
              <span className="sidebar-action-icon">
                <div className="user-avatar" style={{ width: 22, height: 22, fontSize: 8 }}>
                  {(user.email ?? user.sub ?? '??').substring(0, 2).toUpperCase()}
                </div>
              </span>
            </button>
          )}
        </div>

        {/* Collapse toggle */}
        <button
          type="button"
          onClick={toggle}
          className="sidebar-collapse-btn"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand (⌘[)' : 'Collapse (⌘[)'}
        >
          <svg
            width="14"
            height="14"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
            style={{ transform: collapsed ? 'rotate(180deg)' : undefined, transition: 'transform 0.2s ease' }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.75 19.5l-7.5-7.5 7.5-7.5m-6 15L5.25 12l7.5-7.5" />
          </svg>
        </button>
      </div>
    </aside>
  );
}
